import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ChatChannelListResult, ChatChannelResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Must match GLOBAL_CHANNEL_ID in @fias/db-types */
const GLOBAL_CHANNEL_ID = 'cch_global_fias_community';

interface BumpLastMessage {
  content: string;
  senderId: string;
  senderName: string | null;
  createdAt: string;
}

interface UseChatChannelsReturn {
  channels: ChatChannelResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  bumpChannel: (channelId: string, lastMessage: BumpLastMessage) => void;
  createChannel: (input: {
    channelType: 'group' | 'guild';
    name: string;
    description?: string;
  }) => Promise<ChatChannelResponse | null>;
  openDirectMessage: (targetUserId: string) => Promise<ChatChannelResponse | null>;
}

export function useChatChannels(): UseChatChannelsReturn {
  const queryClient = useQueryClient();
  const channelsQueryKey = queryKeys.chat.channels();
  const hasAutoJoinedGlobal = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: channelsQueryKey,
    queryFn: () => api.listChatChannels(),
  });

  // Auto-join global channel side effect
  useEffect(() => {
    if (!data || hasAutoJoinedGlobal.current) return;
    hasAutoJoinedGlobal.current = true;

    const hasGlobal = data.channels.some((c) => c.channelId === GLOBAL_CHANNEL_ID);
    if (hasGlobal) return;

    api
      .joinChatChannel(GLOBAL_CHANNEL_ID)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: channelsQueryKey });
      })
      .catch(() => {
        // Global channel may not exist yet (migration not run) — ignore
      });
  }, [data, queryClient, channelsQueryKey]);

  const bumpChannel = useCallback(
    (channelId: string, lastMessage: BumpLastMessage) => {
      queryClient.setQueryData<ChatChannelListResult>(channelsQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          channels: old.channels.map((ch) =>
            ch.channelId === channelId ? { ...ch, lastMessage } : ch,
          ),
        };
      });
    },
    [queryClient, channelsQueryKey],
  );

  const createChannelMut = useMutation({
    mutationFn: (input: { channelType: 'group' | 'guild'; name: string; description?: string }) =>
      api.createChatChannel(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });

  const openDMMut = useMutation({
    mutationFn: (targetUserId: string) => api.getOrCreateDirectChannel(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: channelsQueryKey });
    },
  });

  return {
    channels: data?.channels ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load channels' : null,
    refresh: () => {
      refetch();
    },
    bumpChannel,
    createChannel: async (input) => {
      try {
        const result = await createChannelMut.mutateAsync(input);
        return result.channel;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create channel';
        throw new Error(message);
      }
    },
    openDirectMessage: async (targetUserId: string) => {
      try {
        const result = await openDMMut.mutateAsync(targetUserId);
        return result.channel;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to open direct message';
        throw new Error(message);
      }
    },
  };
}
